import { LocalNotifications } from "@capacitor/local-notifications";
import { registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import type {
  AudioLevelEvent,
  NativeLiveRecordingPlugin,
} from "./useLiveRecorder";
import { useNativeLiveRecorder } from "./useLiveRecorder";
import { attachIosInterruptionListeners } from "./lifecycle";

export interface LiveRecordingPlugin extends NativeLiveRecordingPlugin {
  getQueueStatus(): Promise<{
    pendingChunks: number;
    pendingBytes?: number;
    hasNetworkError: boolean;
    isNetworkAvailable: boolean;
    isLocalBackupReady?: boolean;
    hasDataLoss?: boolean;
  }>;
  addListener(
    eventName: "onAudioLevelChange",
    listenerFunc: (event: AudioLevelEvent) => void
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: "onInterruptionBegan",
    listenerFunc: () => void
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: "onInterruptionEnded",
    listenerFunc: () => void
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
}

const LiveRecording = registerPlugin<LiveRecordingPlugin>("LiveRecording");
const reliabilityNotificationId = 12345;

export const useLiveRecorderIOS = () => {
  const recorder = useNativeLiveRecorder({
    platform: "ios",
    plugin: LiveRecording,
    reliabilityNotificationId,
    pauseElapsedWhileInactive: true,
    canCancelWhileUploading: true,
    addExtraListeners: async ({
      addListenerHandle,
      appIsActiveRef,
      durationRef,
      plugin,
      startTimeRef,
      t,
    }) => {
      const interruptionListeners = await attachIosInterruptionListeners(
        plugin,
        {
          onBegan: () => {
          if (startTimeRef.current) {
            durationRef.current += Date.now() - startTimeRef.current;
            startTimeRef.current = null;
          }
          },
          onEnded: async () => {
          if (appIsActiveRef.current) {
            return;
          }

          try {
            await LocalNotifications.schedule({
              notifications: [
                {
                  title: t("useLiveRecorder.interruptionEndedTitle"),
                  body: t("useLiveRecorder.interruptionEndedBody"),
                  id: Math.floor(Math.random() * 90000) + 10000,
                },
              ],
            });
          } catch (error) {
            console.error("Failed to schedule interruption notification:", error);
          }
          },
        }
      );
      interruptionListeners.forEach(addListenerHandle);
    },
  });

  return {
    ...recorder,
    getQueueStatus: () => LiveRecording.getQueueStatus(),
  };
};
